import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '@matix/auth';
import {
  deliveryManagerAPI,
  getApiErrorMessage,
  type DeliveryAction,
  type DeliveryBoardOrderDto,
  type DeliveryStatusChangeDto,
} from '@matix/api-client';
import { useLang } from '../../lib/language';
import { toast } from '../../store/toast';
import { moneyPrecision } from '../../store/currency';
import { useTenantConfig } from '../../store/tenantConfig';
import { useCurrency } from '../print/useCurrency';
import { useReprintReceipt } from '../print';
import { LinkIcon, PrinterIcon, RefreshIcon, RouteIcon, SearchIcon } from '../../components/icons';
import { useTick } from './clock';
import {
  ORDER_TYPE,
  ORDER_TYPE_LABEL,
  STATUS,
  STATUS_LABEL,
  TABS,
  areaOf,
  customerOf,
  formatSpan,
  formatStamp,
  matches,
  orderNo,
  pick,
  timelineSteps,
  urgencyOf,
  wallMs,
  type TabKey,
} from './model';
import { playChime, useDeliveryBoard } from './useDeliveryBoard';
import { DispatchDialog } from './DispatchDialog';
import { DeliveryPayDialog } from './DeliveryPayDialog';
import { LinkDriverDialog } from './LinkDriverDialog';
import { printDeliveryReport, reportOrders, type ReportKind } from './reports';
import './deliveryManager.css';

/**
 * The Delivery Manager (مدير التوصيل) — the legacy POS screen /Delivery/Manager.
 *
 * Same board as legacy: date / branch / order-type filters; the New → Ready →
 * Waiting for pickup / Delivery in progress → Delivered / Returned tabs with
 * their counts and their buttons; the driver (and chef) dialog; payment on
 * delivery; scan-to-link; the Delivery, Driver and Chef reports; the timeline;
 * a chime on a new order. Around it, what a busy counter needs: a live summary,
 * per-driver load, search, late-only, row details, bulk actions and keyboard
 * shortcuts.
 *
 * Every write is the server's to allow (the workflow lives in pos-api's
 * deliveryManager.rules.ts); a button here that the server refuses just
 * refreshes the board and says why.
 */

const MUTE_KEY = 'pos:delivery-manager:muted';

function todayLocalKey(offsetMs: number): string {
  return new Date(Date.now() + offsetMs).toISOString().slice(0, 10);
}
function shiftDay(key: string, days: number): string {
  const d = new Date(`${key}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

export function DeliveryManagerScreen(): JSX.Element {
  const { t, lang } = useLang();
  const { cashier } = useAuth();
  const currency = useCurrency();
  const precision = moneyPrecision();
  const reprint = useReprintReceipt();
  const cfg = useTenantConfig();

  const money = useCallback(
    (n: number) => `${(Number.isFinite(n) ? n : 0).toFixed(Math.max(0, Math.min(8, precision)))} ${currency.code}`,
    [precision, currency.code],
  );

  // ── Filters ──────────────────────────────────────────────────────────────
  const [date, setDate] = useState<string | null>(null);
  const [branchId, setBranchId] = useState<number>(cashier?.branch_id ?? 0);
  const [types, setTypes] = useState<number[]>([ORDER_TYPE.Delivery]);
  const [tab, setTab] = useState<TabKey>('new');
  const [search, setSearch] = useState('');
  const [driverFilter, setDriverFilter] = useState<number | null>(null);
  const [lateOnly, setLateOnly] = useState(false);
  const [muted, setMuted] = useState<boolean>(() => {
    try {
      return localStorage.getItem(MUTE_KEY) === '1';
    } catch {
      return false;
    }
  });
  const mutedRef = useRef(muted);
  mutedRef.current = muted;

  const params = useMemo(
    () => ({
      ...(date ? { date } : {}),
      branch_id: branchId,
      order_types: types.join(','),
    }),
    [date, branchId, types],
  );
  const { board, loading, error, loadedAt, clockOffset, refresh, patch } = useDeliveryBoard(params, {
    onNewOrder: () => {
      if (!mutedRef.current) playChime();
      toast(t({ en: 'New delivery order', ar: 'طلب توصيل جديد' }), 'success');
    },
  });
  const effectiveDate = date ?? board?.date ?? todayLocalKey(clockOffset);
  const isToday = effectiveDate === todayLocalKey(clockOffset);

  // Order-type boxes, exactly the ones the tenant's order types allow — as legacy.
  const typeOptions = useMemo(() => {
    const opts: Array<{ code: number; en: string; ar: string }> = [];
    if (cfg.isDelivery) opts.push({ code: ORDER_TYPE.Delivery, en: 'Delivery', ar: 'توصيل' });
    if (cfg.isPickUp) opts.push({ code: ORDER_TYPE.Pickup, en: 'Pickup', ar: 'استلام' });
    if (cfg.isDineIn) opts.push({ code: ORDER_TYPE.DineIn, en: 'Dine in', ar: 'محلي' });
    if (cfg.isTakeAway) {
      opts.push(
        cfg.isRestaurant
          ? { code: ORDER_TYPE.TakeAway, en: 'Take away', ar: 'سفري' }
          : { code: ORDER_TYPE.TakeAway, en: 'Walk-in', ar: 'عميل مباشر' },
      );
    }
    if (!opts.some((o) => o.code === ORDER_TYPE.Delivery)) opts.unshift({ code: ORDER_TYPE.Delivery, en: 'Delivery', ar: 'توصيل' });
    return opts;
  }, [cfg.isDelivery, cfg.isPickUp, cfg.isDineIn, cfg.isTakeAway, cfg.isRestaurant]);

  const toggleType = (code: number) =>
    setTypes((prev) => {
      const next = prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code];
      return next.length ? next.sort() : [ORDER_TYPE.Delivery];
    });

  // ── Derived board ────────────────────────────────────────────────────────
  const orders = useMemo(() => board?.orders ?? [], [board]);
  const nowMs = Date.now() + clockOffset;

  const filtered = useMemo(
    () =>
      orders.filter(
        (o) =>
          matches(o, search) &&
          (driverFilter == null || o.driver_id === driverFilter) &&
          (!lateOnly || urgencyOf(o, Date.now() + clockOffset) !== 'ok'),
      ),
    // loadedAt: "late" is re-judged against the clock on every poll.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [orders, search, driverFilter, lateOnly, clockOffset, loadedAt],
  );

  const counts = useMemo(() => {
    const c: Record<number, number> = {};
    for (const o of filtered) c[o.status] = (c[o.status] ?? 0) + 1;
    return c;
  }, [filtered]);

  const tabMeta = TABS.find((x) => x.key === tab)!;
  const rows = useMemo(
    () => (tabMeta.status == null ? filtered : filtered.filter((o) => o.status === tabMeta.status)),
    [filtered, tabMeta.status],
  );

  const kpi = useMemo(() => {
    const active = orders.filter((o) => o.status < STATUS.Delivered);
    return {
      total: orders.length,
      active: active.length,
      late: active.filter((o) => urgencyOf(o, nowMs) === 'late').length,
      onRoad: orders.filter((o) => o.status === STATUS.OnTheWay).length,
      toCollect: active.reduce((s, o) => s + o.unpaid_amount, 0),
      delivered: orders.filter((o) => o.status === STATUS.Delivered).length,
    };
    // Recomputed per poll; "late" moving by a minute between polls is fine.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orders, loadedAt]);

  const driverLoad = useMemo(() => {
    const m = new Map<number, { id: number; name: string; count: number; cash: number }>();
    for (const o of orders) {
      if (o.driver_id == null || o.status !== STATUS.OnTheWay) continue;
      const cur = m.get(o.driver_id) ?? {
        id: o.driver_id,
        name: pick(lang, o.driver_name, o.driver_name_ar) || `#${o.driver_id}`,
        count: 0,
        cash: 0,
      };
      cur.count += 1;
      cur.cash += o.unpaid_amount;
      m.set(o.driver_id, cur);
    }
    return [...m.values()].sort((a, b) => b.count - a.count);
  }, [orders, lang]);

  // ── Selection (bulk) ─────────────────────────────────────────────────────
  const [selected, setSelected] = useState<Set<number>>(new Set());
  useEffect(() => setSelected(new Set()), [tab, params]);
  const selectable = (o: DeliveryBoardOrderDto) =>
    (tab === 'new' && o.status === STATUS.New) ||
    (tab === 'ready' && o.status === STATUS.Ready && o.order_type === ORDER_TYPE.Delivery);
  const selectableRows = rows.filter(selectable);
  const selectedRows = rows.filter((o) => selected.has(o.id));
  const toggleSel = (id: number) =>
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  // ── Actions ──────────────────────────────────────────────────────────────
  const [busyId, setBusyId] = useState<number | null>(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [dispatchFor, setDispatchFor] = useState<DeliveryBoardOrderDto | null>(null);
  const [bulkDispatch, setBulkDispatch] = useState(false);
  const [payFor, setPayFor] = useState<DeliveryBoardOrderDto | null>(null);
  const [returnFor, setReturnFor] = useState<DeliveryBoardOrderDto | null>(null);
  const [linkOpen, setLinkOpen] = useState(false);
  const [expanded, setExpanded] = useState<number | null>(null);
  const [reportBusy, setReportBusy] = useState<ReportKind | null>(null);

  const send = useCallback(
    async (o: DeliveryBoardOrderDto, body: Omit<DeliveryStatusChangeDto, 'expected_status'>, quiet = false) => {
      setBusyId(o.id);
      try {
        const res = await deliveryManagerAPI.changeStatus(o.id, { ...body, expected_status: o.status });
        patch(res.order);
        if (!quiet) {
          const label = STATUS_LABEL[res.order.status];
          toast(`${orderNo(o)} → ${label ? t(label) : ''}`, 'success');
        }
        return true;
      } catch (err) {
        toast(getApiErrorMessage(err), 'error');
        refresh();
        return false;
      } finally {
        setBusyId(null);
      }
    },
    [patch, refresh, t],
  );

  const onReady = (o: DeliveryBoardOrderDto) => void send(o, { action: 'ready' });
  const onDispatch = (o: DeliveryBoardOrderDto) => {
    if (o.order_type === ORDER_TYPE.Delivery) setDispatchFor(o);
    else void send(o, { action: 'dispatch' });
  };
  const onDeliver = (o: DeliveryBoardOrderDto) => {
    if (o.unpaid_amount > 0) {
      if (o.has_call_centre_order) {
        toast(
          t({
            en: 'This is a call-centre order — take its payment on the Pending Orders screen first.',
            ar: 'هذا طلب من مركز الاتصال — حصّل قيمته من شاشة الطلبات قيد الانتظار أولاً.',
          }),
          'error',
        );
        return;
      }
      setPayFor(o);
    } else {
      void send(o, { action: 'deliver' });
    }
  };
  const canReturn = (o: DeliveryBoardOrderDto) =>
    o.code === 52 ? Boolean(board?.can_credit_return) : o.code === 51 ? Boolean(board?.can_cash_return) : true;
  const onReturn = (o: DeliveryBoardOrderDto) => {
    if (!canReturn(o)) {
      toast(t({ en: "You don't have permission to access this.", ar: 'ليس لديك صلاحية للقيام بذلك.' }), 'error');
      return;
    }
    setReturnFor(o);
  };

  async function bulkReady(): Promise<void> {
    setBulkBusy(true);
    let ok = 0;
    for (const o of selectedRows) {
      if (await send(o, { action: 'ready' }, true)) ok++;
    }
    setBulkBusy(false);
    setSelected(new Set());
    toast(t({ en: `${ok} orders marked ready`, ar: `تم تجهيز ${ok} طلبات` }), ok ? 'success' : 'error');
    refresh();
  }

  async function linkMany(driverId: number, chefId: number | null, ids: number[]): Promise<boolean> {
    setBulkBusy(true);
    try {
      const res = await deliveryManagerAPI.linkDriver({ driver_id: driverId, chef_id: chefId, order_ids: ids });
      toast(
        res.skipped.length
          ? t({
              en: `${res.linked.length} orders sent out · ${res.skipped.length} skipped (already moved on)`,
              ar: `تم إرسال ${res.linked.length} طلبات · تم تخطي ${res.skipped.length}`,
            })
          : t({ en: `${res.linked.length} orders sent out with the driver`, ar: `تم إرسال ${res.linked.length} طلبات مع السائق` }),
        res.linked.length ? 'success' : 'error',
      );
      refresh();
      return true;
    } catch (err) {
      toast(getApiErrorMessage(err), 'error');
      return false;
    } finally {
      setBulkBusy(false);
    }
  }

  async function runReport(kind: ReportKind): Promise<void> {
    if (!board) return;
    setReportBusy(kind);
    try {
      const ids = kind === 'delivery' ? [] : reportOrders(kind, board.orders).map((o) => o.id);
      const items = await deliveryManagerAPI.items(ids);
      const branchLabel =
        board.branch_id === 0
          ? t({ en: 'All branches', ar: 'كل الفروع' })
          : board.branches.find((b) => b.id === board.branch_id)?.name ?? '';
      printDeliveryReport({ kind, board, items, branchLabel, lang, money });
    } catch (err) {
      toast(getApiErrorMessage(err), 'error');
    } finally {
      setReportBusy(null);
    }
  }

  // ── Keyboard: 1–7 tabs · / search · R refresh ────────────────────────────
  const searchRef = useRef<HTMLInputElement>(null);
  const modalOpen = Boolean(dispatchFor || bulkDispatch || payFor || returnFor || linkOpen);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (modalOpen || e.ctrlKey || e.metaKey || e.altKey) return;
      const el = e.target as HTMLElement | null;
      if (el && (el.tagName === 'INPUT' || el.tagName === 'SELECT' || el.tagName === 'TEXTAREA')) return;
      if (e.key >= '1' && e.key <= '7') {
        setTab(TABS[Number(e.key) - 1]!.key);
      } else if (e.key === '/') {
        e.preventDefault();
        searchRef.current?.focus();
      } else if (e.key === 'r' || e.key === 'R') {
        refresh();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [modalOpen, refresh]);

  const toggleMute = () =>
    setMuted((m) => {
      try {
        localStorage.setItem(MUTE_KEY, m ? '0' : '1');
      } catch {
        /* per-viewer convenience only */
      }
      return !m;
    });

  const hasChefs = (board?.chefs.length ?? 0) > 0;
  const showCategory = Boolean(board?.has_categories);

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="dm-screen">
      <header className="dm-head">
        <div className="dm-title-wrap">
          <h1 className="dm-title">
            <RouteIcon size={22} /> {t({ en: 'Delivery Manager', ar: 'مدير التوصيل' })}
          </h1>
          <LiveStamp loadedAt={loadedAt} loading={loading} error={error} />
        </div>
        <div className="dm-head-actions">
          <button type="button" className="dm-btn dm-btn--success" onClick={() => setLinkOpen(true)} disabled={!board}>
            <LinkIcon size={16} /> {t({ en: 'Link orders to driver', ar: 'ربط الطلبات بالسائق' })}
          </button>
          <button type="button" className="dm-btn" onClick={() => void runReport('delivery')} disabled={!board || reportBusy != null}>
            <PrinterIcon size={16} /> {t({ en: 'Delivery report', ar: 'تقرير التوصيل' })}
          </button>
          <button type="button" className="dm-btn" onClick={() => void runReport('driver')} disabled={!board || reportBusy != null}>
            <PrinterIcon size={16} /> {t({ en: 'Driver report', ar: 'تقرير السائقين' })}
          </button>
          {hasChefs && (
            <button type="button" className="dm-btn" onClick={() => void runReport('chef')} disabled={!board || reportBusy != null}>
              <PrinterIcon size={16} /> {t({ en: 'Chef report', ar: 'تقرير الشيف' })}
            </button>
          )}
          <button
            type="button"
            className={`dm-icon-btn ${muted ? '' : 'is-on'}`}
            onClick={toggleMute}
            title={muted ? t({ en: 'Sound off', ar: 'الصوت مغلق' }) : t({ en: 'Sound on', ar: 'الصوت مفعل' })}
            aria-pressed={!muted}
          >
            {muted ? '🔕' : '🔔'}
          </button>
          <button type="button" className={`dm-icon-btn ${loading ? 'is-spinning' : ''}`} onClick={refresh} title={t({ en: 'Refresh (R)', ar: 'تحديث (R)' })}>
            <RefreshIcon size={16} />
          </button>
        </div>
      </header>

      {/* Filters — the legacy three, plus search and late-only. */}
      <div className="dm-filters">
        <div className="dm-date">
          <button type="button" className="dm-icon-btn" onClick={() => setDate(shiftDay(effectiveDate, -1))} aria-label={t({ en: 'Previous day', ar: 'اليوم السابق' })}>
            {lang === 'ar' ? '›' : '‹'}
          </button>
          <input
            type="date"
            className="cc-input dm-date-input"
            value={effectiveDate}
            onChange={(e) => e.target.value && setDate(e.target.value)}
          />
          <button type="button" className="dm-icon-btn" onClick={() => setDate(shiftDay(effectiveDate, 1))} aria-label={t({ en: 'Next day', ar: 'اليوم التالي' })}>
            {lang === 'ar' ? '‹' : '›'}
          </button>
          {!isToday && (
            <button type="button" className="dm-link" onClick={() => setDate(null)}>
              {t({ en: 'Today', ar: 'اليوم' })}
            </button>
          )}
        </div>

        <select className="cc-select dm-branch" value={branchId} onChange={(e) => setBranchId(Number(e.target.value))}>
          <option value={0}>{t({ en: 'All branches', ar: 'كل الفروع' })}</option>
          {(board?.branches ?? []).map((b) => (
            <option key={b.id} value={b.id}>
              {b.name ?? `#${b.id}`}
            </option>
          ))}
        </select>

        <div className="dm-types" role="group" aria-label={t({ en: 'Order type', ar: 'نوع الطلب' })}>
          {typeOptions.map((o) => (
            <button
              key={o.code}
              type="button"
              className={`dm-chip ${types.includes(o.code) ? 'is-on' : ''}`}
              aria-pressed={types.includes(o.code)}
              onClick={() => toggleType(o.code)}
            >
              {t(o)}
            </button>
          ))}
        </div>

        <label className="dm-search">
          <SearchIcon size={16} />
          <input
            ref={searchRef}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t({ en: 'Order no, customer, phone, area… ( / )', ar: 'رقم الطلب، العميل، الهاتف، المنطقة… ( / )' })}
          />
          {search && (
            <button type="button" onClick={() => setSearch('')} aria-label={t({ en: 'Clear', ar: 'مسح' })}>
              ×
            </button>
          )}
        </label>

        <button type="button" className={`dm-chip dm-chip--warn ${lateOnly ? 'is-on' : ''}`} onClick={() => setLateOnly((v) => !v)} aria-pressed={lateOnly}>
          ⏱ {t({ en: 'Late / due soon', ar: 'متأخر / قريب الموعد' })}
        </button>
      </div>

      {/* Live summary */}
      <div className="dm-kpis">
        <Kpi label={t({ en: 'Orders', ar: 'الطلبات' })} value={String(kpi.total)} />
        <Kpi label={t({ en: 'Open', ar: 'قيد التنفيذ' })} value={String(kpi.active)} />
        <Kpi label={t({ en: 'Late', ar: 'متأخرة' })} value={String(kpi.late)} tone={kpi.late ? 'danger' : undefined} onClick={() => setLateOnly(true)} />
        <Kpi label={t({ en: 'On the road', ar: 'في الطريق' })} value={String(kpi.onRoad)} tone="violet" onClick={() => setTab('onTheWay')} />
        <Kpi label={t({ en: 'To collect', ar: 'مبالغ للتحصيل' })} value={money(kpi.toCollect)} tone={kpi.toCollect > 0 ? 'amber' : undefined} />
        <Kpi label={t({ en: 'Delivered', ar: 'تم التوصيل' })} value={String(kpi.delivered)} tone="success" onClick={() => setTab('delivered')} />
      </div>

      {driverLoad.length > 0 && (
        <div className="dm-drivers-bar">
          <span className="dm-drivers-label">{t({ en: 'Drivers out', ar: 'السائقون بالخارج' })}</span>
          {driverLoad.map((d) => (
            <button
              key={d.id}
              type="button"
              className={`dm-driver-chip ${driverFilter === d.id ? 'is-on' : ''}`}
              onClick={() => setDriverFilter((cur) => (cur === d.id ? null : d.id))}
              title={t({ en: 'Show only this driver', ar: 'عرض طلبات هذا السائق فقط' })}
            >
              <b>{d.name}</b>
              <span className="dm-badge">{d.count}</span>
              {d.cash > 0 && <span className="dm-driver-cash">{money(d.cash)}</span>}
            </button>
          ))}
          {driverFilter != null && (
            <button type="button" className="dm-link" onClick={() => setDriverFilter(null)}>
              {t({ en: 'Show all', ar: 'عرض الكل' })}
            </button>
          )}
        </div>
      )}

      {/* Status tabs */}
      <nav className="dm-tabs" role="tablist">
        {TABS.map((x, i) => (
          <button
            key={x.key}
            type="button"
            role="tab"
            aria-selected={tab === x.key}
            className={`dm-tab dm-tone-${x.tone} ${tab === x.key ? 'is-on' : ''}`}
            onClick={() => setTab(x.key)}
            title={`${t(x)} (${i + 1})`}
          >
            <span className="dm-dot" />
            {t(x)}
            {x.status != null && <span className="dm-count">{counts[x.status] ?? 0}</span>}
          </button>
        ))}
      </nav>

      {error && <div className="cc-warn">{error}</div>}

      {selectedRows.length > 0 && (
        <div className="dm-bulk">
          <span>
            {t({ en: `${selectedRows.length} selected`, ar: `تم تحديد ${selectedRows.length}` })}
          </span>
          {tab === 'new' && (
            <button type="button" className="dm-btn dm-btn--primary" disabled={bulkBusy} onClick={() => void bulkReady()}>
              {t({ en: 'Mark ready', ar: 'تجهيز' })}
            </button>
          )}
          {tab === 'ready' && (
            <button type="button" className="dm-btn dm-btn--primary" disabled={bulkBusy} onClick={() => setBulkDispatch(true)}>
              {t({ en: 'Send with driver…', ar: 'إرسال مع سائق…' })}
            </button>
          )}
          <button type="button" className="dm-link" onClick={() => setSelected(new Set())}>
            {t({ en: 'Clear', ar: 'مسح' })}
          </button>
        </div>
      )}

      <div className="dm-table-wrap">
        {tab === 'timeline' ? (
          <TimelineTable rows={rows} money={money} />
        ) : (
          <table className="dm-table">
            <thead>
              <tr>
                {(tab === 'new' || tab === 'ready') && (
                  <th className="dm-sel">
                    <input
                      type="checkbox"
                      aria-label={t({ en: 'Select all', ar: 'تحديد الكل' })}
                      checked={selectableRows.length > 0 && selectableRows.every((o) => selected.has(o.id))}
                      disabled={selectableRows.length === 0}
                      onChange={(e) => setSelected(e.target.checked ? new Set(selectableRows.map((o) => o.id)) : new Set())}
                    />
                  </th>
                )}
                <th>{t({ en: 'Order no', ar: 'رقم الطلب' })}</th>
                <th>{t({ en: 'Customer', ar: 'العميل' })}</th>
                <th>{t({ en: 'Phone', ar: 'الهاتف' })}</th>
                {showCategory && <th>{t({ en: 'Category', ar: 'الفئة' })}</th>}
                {tab !== 'new' && tab !== 'ready' && tab !== 'waiting' && <th>{t({ en: 'Driver', ar: 'السائق' })}</th>}
                <th>{t({ en: 'Date', ar: 'التاريخ' })}</th>
                <th>{t({ en: 'Elapsed', ar: 'الوقت المنقضي' })}</th>
                {(tab === 'new' || tab === 'ready') && <th>{t({ en: 'Delivery', ar: 'موعد التوصيل' })}</th>}
                <th>{t({ en: 'Remaining', ar: 'المتبقي' })}</th>
                <th>{t({ en: 'Area', ar: 'المنطقة' })}</th>
                <th>{t({ en: 'Payment', ar: 'الدفع' })}</th>
                <th className="dm-num">{t({ en: 'Amount', ar: 'المبلغ' })}</th>
                <th className="dm-actions-h" />
              </tr>
            </thead>
            <tbody>
              {rows.map((o) => {
                const urgency = urgencyOf(o, nowMs);
                const isOpen = expanded === o.id;
                const busy = busyId === o.id || bulkBusy;
                const colCount =
                  10 +
                  (tab === 'new' || tab === 'ready' ? 2 : 0) +
                  (showCategory ? 1 : 0) +
                  (tab !== 'new' && tab !== 'ready' && tab !== 'waiting' ? 1 : 0);
                return (
                  <Fragment key={o.id}>
                    <tr
                      className={`dm-row dm-u-${urgency} ${isOpen ? 'is-open' : ''} ${selected.has(o.id) ? 'is-selected' : ''}`}
                      onClick={(e) => {
                        if ((e.target as HTMLElement).closest('button, input, a')) return;
                        setExpanded(isOpen ? null : o.id);
                      }}
                    >
                      {(tab === 'new' || tab === 'ready') && (
                        <td className="dm-sel">
                          <input type="checkbox" checked={selected.has(o.id)} disabled={!selectable(o)} onChange={() => toggleSel(o.id)} />
                        </td>
                      )}
                      <td className="dm-no">
                        <b>{orderNo(o)}</b>
                        {o.order_type !== ORDER_TYPE.Delivery && o.order_type != null && (
                          <span className="dm-type">{t(ORDER_TYPE_LABEL[o.order_type] ?? { en: '', ar: '' })}</span>
                        )}
                      </td>
                      <td className="dm-cust">{customerOf(o, lang) || '—'}</td>
                      <td className="dm-nowrap">
                        {o.customer_phone ? (
                          <a href={`tel:${o.customer_phone}`} className="dm-phone">
                            {o.customer_phone}
                          </a>
                        ) : (
                          '—'
                        )}
                      </td>
                      {showCategory && <td>{o.category ?? ''}</td>}
                      {tab !== 'new' && tab !== 'ready' && tab !== 'waiting' && (
                        <td className="dm-nowrap">
                          {tab === 'onTheWay' ? (
                            <button type="button" className="dm-link" disabled={busy} onClick={() => setDispatchFor(o)} title={t({ en: 'Change driver', ar: 'تغيير السائق' })}>
                              {pick(lang, o.driver_name, o.driver_name_ar) || t({ en: 'Assign', ar: 'تعيين' })}
                            </button>
                          ) : (
                            pick(lang, o.driver_name, o.driver_name_ar) || '—'
                          )}
                        </td>
                      )}
                      <td className="dm-nowrap">{formatStamp(o.entry_date, lang)}</td>
                      <td className="dm-nowrap dm-num">
                        <Elapsed order={o} offset={clockOffset} />
                      </td>
                      {(tab === 'new' || tab === 'ready') && <td className="dm-nowrap">{formatStamp(o.delivery_date ?? o.entry_date, lang)}</td>}
                      <td className="dm-nowrap dm-num">
                        <Remaining order={o} offset={clockOffset} />
                      </td>
                      <td>{areaOf(o, lang) || '—'}</td>
                      <td className="dm-nowrap">
                        {o.unpaid_amount > 0 ? (
                          <span className="dm-pill dm-pill--unpaid">{pick(lang, o.payment_name, o.payment_name_ar) || t({ en: 'Unpaid', ar: 'غير مدفوع' })}</span>
                        ) : (
                          <span className="dm-pill">{pick(lang, o.payment_name, o.payment_name_ar) || '—'}</span>
                        )}
                      </td>
                      <td className="dm-num dm-amount">{money(o.amount)}</td>
                      <td className="dm-actions">
                        <RowActions
                          order={o}
                          tab={tab}
                          busy={busy}
                          canReturn={canReturn(o)}
                          onReady={onReady}
                          onDispatch={onDispatch}
                          onDeliver={onDeliver}
                          onReturn={onReturn}
                          onPrint={(x) => void reprint(x.id)}
                        />
                      </td>
                    </tr>
                    {isOpen && (
                      <tr className="dm-detail-row">
                        <td colSpan={colCount}>
                          <OrderDetail order={o} money={money} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        )}

        {!loading && rows.length === 0 && (
          <div className="dm-empty">
            <div className="dm-empty-title">
              {search || driverFilter != null || lateOnly
                ? t({ en: 'Nothing matches the filters', ar: 'لا يوجد ما يطابق الفلاتر' })
                : t({ en: `No ${t(tabMeta).toLowerCase()} orders`, ar: `لا توجد طلبات — ${tabMeta.ar}` })}
            </div>
            <p>
              {t({
                en: 'Orders appear here by their promised delivery date. Change the date, branch or order types above to see others.',
                ar: 'تظهر الطلبات هنا حسب موعد التوصيل. غيّر التاريخ أو الفرع أو نوع الطلب لعرض غيرها.',
              })}
            </p>
          </div>
        )}
        {loading && !board && <div className="dm-empty">{t({ en: 'Loading…', ar: 'جارٍ التحميل…' })}</div>}
      </div>

      <div className="dm-legend">
        <span><i className="dm-sw dm-sw--soon" /> {t({ en: 'Due within 15 minutes', ar: 'خلال 15 دقيقة' })}</span>
        <span><i className="dm-sw dm-sw--late" /> {t({ en: 'Late', ar: 'متأخر' })}</span>
        <span className="dm-keys">{t({ en: 'Keys: 1–7 tabs · / search · R refresh · click a row for details', ar: 'اختصارات: 1–7 التبويبات · / بحث · R تحديث · اضغط على الصف للتفاصيل' })}</span>
      </div>

      {/* Dialogs */}
      {dispatchFor && board && (
        <DispatchDialog
          order={dispatchFor}
          drivers={board.drivers}
          chefs={board.chefs}
          busy={busyId === dispatchFor.id}
          onClose={() => setDispatchFor(null)}
          onConfirm={(driverId, chefId) => {
            const o = dispatchFor;
            void send(o, { action: 'dispatch', driver_id: driverId, chef_id: chefId }).then((ok) => ok && setDispatchFor(null));
          }}
        />
      )}
      {bulkDispatch && board && selectedRows[0] && (
        <DispatchDialog
          order={selectedRows[0]}
          count={selectedRows.length}
          drivers={board.drivers}
          chefs={board.chefs}
          busy={bulkBusy}
          onClose={() => setBulkDispatch(false)}
          onConfirm={(driverId, chefId) => {
            void linkMany(driverId, chefId, selectedRows.map((o) => o.id)).then((ok) => {
              if (ok) {
                setBulkDispatch(false);
                setSelected(new Set());
              }
            });
          }}
        />
      )}
      {payFor && (
        <DeliveryPayDialog
          order={payFor}
          busy={busyId === payFor.id}
          onClose={() => setPayFor(null)}
          onConfirm={(payments) => {
            const o = payFor;
            void send(o, { action: 'deliver', payments }).then((ok) => ok && setPayFor(null));
          }}
        />
      )}
      {returnFor && (
        <ConfirmReturn
          order={returnFor}
          money={money}
          busy={busyId === returnFor.id}
          onClose={() => setReturnFor(null)}
          onConfirm={() => {
            const o = returnFor;
            void send(o, { action: 'return' }).then((ok) => ok && setReturnFor(null));
          }}
        />
      )}
      {linkOpen && board && (
        <LinkDriverDialog
          orders={board.orders}
          drivers={board.drivers}
          chefs={board.chefs}
          busy={bulkBusy}
          money={money}
          onClose={() => setLinkOpen(false)}
          onConfirm={(driverId, chefId, ids) => {
            void linkMany(driverId, chefId, ids).then((ok) => ok && setLinkOpen(false));
          }}
        />
      )}
    </div>
  );
}

// ── Pieces ─────────────────────────────────────────────────────────────────

function LiveStamp({ loadedAt, loading, error }: { loadedAt: number | null; loading: boolean; error: string | null }): JSX.Element {
  const { t } = useLang();
  const tick = useTick();
  const secs = loadedAt ? Math.max(0, Math.round((tick - loadedAt) / 1000)) : null;
  return (
    <div className={`dm-live ${error ? 'is-error' : ''}`}>
      <span className="dm-live-dot" />
      {error
        ? t({ en: 'Connection problem — retrying', ar: 'مشكلة في الاتصال — جارٍ إعادة المحاولة' })
        : loading && secs == null
          ? t({ en: 'Loading…', ar: 'جارٍ التحميل…' })
          : t({ en: `Live · updated ${secs ?? 0}s ago`, ar: `مباشر · آخر تحديث قبل ${secs ?? 0} ث` })}
    </div>
  );
}

function Kpi({ label, value, tone, onClick }: { label: string; value: string; tone?: 'danger' | 'amber' | 'violet' | 'success'; onClick?: () => void }): JSX.Element {
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag type={onClick ? 'button' : undefined} className={`dm-kpi ${tone ? `dm-kpi--${tone}` : ''}`} onClick={onClick}>
      <span className="dm-kpi-label">{label}</span>
      <span className="dm-kpi-value">{value}</span>
    </Tag>
  );
}

/** Elapsed since the order was taken — frozen at delivery for a delivered order. */
function Elapsed({ order, offset }: { order: DeliveryBoardOrderDto; offset: number }): JSX.Element {
  const tick = useTick();
  const start = wallMs(order.entry_date);
  if (start == null) return <>—</>;
  const end = order.status === STATUS.Delivered ? wallMs(order.delivered_at) ?? tick + offset : tick + offset;
  return <>{formatSpan(end - start)}</>;
}

/** Time to the promised delivery; "(-)" once it has passed, as legacy showed it. */
function Remaining({ order, offset }: { order: DeliveryBoardOrderDto; offset: number }): JSX.Element {
  const tick = useTick();
  const due = wallMs(order.delivery_date) ?? wallMs(order.entry_date);
  if (due == null) return <>—</>;
  const ref = order.status === STATUS.Delivered ? wallMs(order.delivered_at) ?? tick + offset : tick + offset;
  const late = ref > due;
  return <span className={late && order.status < STATUS.Delivered ? 'dm-late-text' : ''}>{late ? '(-) ' : ''}{formatSpan(due - ref)}</span>;
}

function RowActions({
  order: o,
  tab,
  busy,
  canReturn,
  onReady,
  onDispatch,
  onDeliver,
  onReturn,
  onPrint,
}: {
  order: DeliveryBoardOrderDto;
  tab: TabKey;
  busy: boolean;
  canReturn: boolean;
  onReady: (o: DeliveryBoardOrderDto) => void;
  onDispatch: (o: DeliveryBoardOrderDto) => void;
  onDeliver: (o: DeliveryBoardOrderDto) => void;
  onReturn: (o: DeliveryBoardOrderDto) => void;
  onPrint: (o: DeliveryBoardOrderDto) => void;
}): JSX.Element {
  const { t } = useLang();
  const isDelivery = o.order_type === ORDER_TYPE.Delivery;
  const action = (a: DeliveryAction, label: { en: string; ar: string }, cls: string, fn: () => void) => (
    <button key={a} type="button" className={`dm-btn dm-btn--sm ${cls}`} disabled={busy} onClick={fn}>
      {t(label)}
    </button>
  );
  const buttons: JSX.Element[] = [];
  if (tab === 'new') buttons.push(action('ready', { en: 'Ready', ar: 'جاهز' }, 'dm-btn--primary', () => onReady(o)));
  if (tab === 'ready') {
    buttons.push(
      action(
        'dispatch',
        isDelivery ? { en: 'Delivery in progress', ar: 'جاري التوصيل' } : { en: 'Waiting for pickup', ar: 'بانتظار الاستلام' },
        'dm-btn--primary',
        () => onDispatch(o),
      ),
    );
  }
  if (tab === 'waiting' || tab === 'onTheWay') {
    buttons.push(
      action(
        'deliver',
        o.unpaid_amount > 0 ? { en: 'Collect & deliver', ar: 'تحصيل وتسليم' } : { en: 'Delivered', ar: 'تم التوصيل' },
        'dm-btn--success',
        () => onDeliver(o),
      ),
    );
    if (tab === 'onTheWay' || !isDelivery) {
      buttons.push(
        <button
          key="return"
          type="button"
          className="dm-btn dm-btn--sm dm-btn--warn"
          disabled={busy || !canReturn}
          title={canReturn ? undefined : t({ en: "You don't have permission to access this.", ar: 'ليس لديك صلاحية للقيام بذلك.' })}
          onClick={() => onReturn(o)}
        >
          {t({ en: 'Returned', ar: 'مرتجع' })}
        </button>,
      );
    }
  }
  buttons.push(
    <button key="print" type="button" className="dm-btn dm-btn--sm dm-btn--ghost" onClick={() => onPrint(o)} title={t({ en: 'Print', ar: 'طباعة' })}>
      <PrinterIcon size={14} />
    </button>,
  );
  return <div className="dm-actions-row">{buttons}</div>;
}

function OrderDetail({ order: o, money }: { order: DeliveryBoardOrderDto; money: (n: number) => string }): JSX.Element {
  const { t, lang } = useLang();
  const steps = timelineSteps(o);
  return (
    <div className="dm-detail">
      <div className="dm-detail-col">
        <DetailRow label={t({ en: 'Address', ar: 'العنوان' })} value={o.address_details} />
        <DetailRow label={t({ en: 'Notes', ar: 'ملاحظات' })} value={o.notes} />
        <DetailRow label={t({ en: 'Driver', ar: 'السائق' })} value={pick(lang, o.driver_name, o.driver_name_ar)} />
        {(o.chef_name || o.chef_name_ar) && <DetailRow label={t({ en: 'Chef', ar: 'الشيف' })} value={pick(lang, o.chef_name, o.chef_name_ar)} />}
      </div>
      <div className="dm-detail-col">
        <DetailRow label={t({ en: 'Delivery time', ar: 'موعد التوصيل' })} value={formatStamp(o.delivery_date, lang)} />
        {o.delivered_at && <DetailRow label={t({ en: 'Delivered at', ar: 'وقت التسليم' })} value={formatStamp(o.delivered_at, lang)} />}
        {o.delivery_fees > 0 && <DetailRow label={t({ en: 'Delivery fee', ar: 'رسوم التوصيل' })} value={money(o.delivery_fees)} />}
        {o.unpaid_amount > 0 && <DetailRow label={t({ en: 'To collect', ar: 'للتحصيل' })} value={money(o.unpaid_amount)} strong />}
      </div>
      <div className="dm-detail-col">
        <div className="dm-detail-label">{t({ en: 'History', ar: 'السجل' })}</div>
        {steps.length === 0 ? (
          <div className="dm-muted">{t({ en: 'No status changes yet', ar: 'لا توجد تغييرات بعد' })}</div>
        ) : (
          <ol className="dm-steps">
            {steps.map((s, i) => (
              <li key={i}>
                <span>{t(STATUS_LABEL[s.status] ?? { en: String(s.status), ar: String(s.status) })}</span>
                <span className="dm-muted">+{s.minutes.toFixed(0)} {t({ en: 'min', ar: 'د' })}</span>
              </li>
            ))}
          </ol>
        )}
      </div>
    </div>
  );
}

function DetailRow({ label, value, strong }: { label: string; value: string | null | undefined; strong?: boolean }): JSX.Element {
  return (
    <div className="dm-detail-item">
      <span className="dm-detail-label">{label}</span>
      <span className={strong ? 'dm-strong' : ''}>{value || '—'}</span>
    </div>
  );
}

const STEP_COLORS = ['#4CAF50', '#6fa1f2', '#e06459', '#a56184', '#7fc48d'];

/** The legacy Timeline tab: one bar per order, a segment per step, widths clamped 20–40%. */
function TimelineTable({ rows, money }: { rows: DeliveryBoardOrderDto[]; money: (n: number) => string }): JSX.Element {
  const { t } = useLang();
  return (
    <table className="dm-table">
      <thead>
        <tr>
          <th>{t({ en: 'Order no', ar: 'رقم الطلب' })}</th>
          <th className="dm-num">{t({ en: 'Amount', ar: 'المبلغ' })}</th>
          <th style={{ width: '55%' }}>{t({ en: 'Timeline', ar: 'الخط الزمني' })}</th>
          <th className="dm-num">{t({ en: 'Total minutes', ar: 'إجمالي الدقائق' })}</th>
          <th>{t({ en: 'Status', ar: 'الحالة' })}</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((o) => {
          const steps = timelineSteps(o);
          const total = steps.reduce((s, x) => s + x.minutes, 0);
          return (
            <tr key={o.id} className="dm-row">
              <td className="dm-no"><b>{orderNo(o)}</b></td>
              <td className="dm-num">{money(o.amount)}</td>
              <td>
                <div className="dm-bar">
                  {steps.map((s, i) => {
                    const pct = total > 0 ? Math.min(40, Math.max(20, (s.minutes / total) * 100)) : 20;
                    const label = t(STATUS_LABEL[s.status] ?? { en: String(s.status), ar: String(s.status) });
                    return (
                      <div key={i} className="dm-bar-seg" style={{ width: `${pct}%`, background: STEP_COLORS[Math.min(i, 4)] }} title={`${label}: ${s.minutes.toFixed(2)} min`}>
                        {s.minutes.toFixed(0)}′ · {label}
                      </div>
                    );
                  })}
                  {steps.length === 0 && <span className="dm-muted">—</span>}
                </div>
              </td>
              <td className="dm-num">{total.toFixed(2)}</td>
              <td>
                <span className={`dm-pill dm-status-${o.status}`}>{t(STATUS_LABEL[o.status] ?? { en: '', ar: '' })}</span>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function ConfirmReturn({
  order,
  money,
  busy,
  onConfirm,
  onClose,
}: {
  order: DeliveryBoardOrderDto;
  money: (n: number) => string;
  busy: boolean;
  onConfirm: () => void;
  onClose: () => void;
}): JSX.Element {
  const { t, lang } = useLang();
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="cc-modal-backdrop" role="dialog" aria-modal="true" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="cc-modal dm-modal">
        <header className="cc-modal-head">
          <div>
            <div className="cc-modal-title">{t({ en: 'Mark as returned?', ar: 'تسجيل كمرتجع؟' })}</div>
            <div className="cc-modal-sub">
              {orderNo(order)} · {customerOf(order, lang)} · {money(order.amount)}
            </div>
          </div>
          <button type="button" className="cc-drawer-close" onClick={onClose} aria-label={t({ en: 'Close', ar: 'إغلاق' })}>
            ×
          </button>
        </header>
        <p className="dm-confirm-text">
          {t({
            en: 'A return receipt is created for the whole order and the items go back into stock. This cannot be undone from this screen.',
            ar: 'سيتم إنشاء فاتورة مرتجع بكامل الطلب وإرجاع الأصناف إلى المخزون. لا يمكن التراجع عن ذلك من هذه الشاشة.',
          })}
        </p>
        <div className="dm-modal-actions">
          <button type="button" className="cc-btn" onClick={onClose}>
            {t({ en: 'Cancel', ar: 'إلغاء' })}
          </button>
          <button type="button" className="cc-btn cc-btn--danger" disabled={busy} onClick={onConfirm}>
            {busy ? t({ en: 'Saving…', ar: 'جارٍ الحفظ…' }) : t({ en: 'Yes, returned', ar: 'نعم، مرتجع' })}
          </button>
        </div>
      </div>
    </div>
  );
}
